// src/pages/ForgotPassword.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import izbLogo from "../assets/izblogo.png";
import IzyaneLogo from "../assets/iZyaneLogo.png";
import rolafaceLogo from "../assets/rolafaceLogo.png";

const ForgotPassword: React.FC = () => {
  const [identifier, setIdentifier] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");

  const navigate = useNavigate();
  const baseurl = import.meta.env.VITE_API_PATH as string;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await fetch(`${baseurl}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ identifier }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to send reset link");
      }

      setSuccess(true);
      setMaskedEmail(data.email);
    } catch (err: any) {
      setError(err.message || "Failed to send reset link");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="mb-8 text-center">
          <img
            src={izbLogo}
            alt="Izb Logo"
            className="h-40 w-auto mx-auto mb-2 object-contain"
          />
          <p className="text-gray-600 mt-2">Reconciliation Management System</p>
        </div>

        {/* Forgot Password Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          {!success ? (
            <>
              <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
                Forgot Password?
              </h2>
              <p className="text-gray-600 text-sm text-center mb-6">
                Enter your username or email and we'll send you a link to reset
                your password.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm text-center">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="identifier"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Username or Email
                  </label>
                  <input
                    type="text"
                    id="identifier"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9D282C] focus:border-transparent transition"
                    placeholder="Enter your username or email"
                    required
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-[#9D282C] text-white w-full py-3 px-4 rounded-lg font-semibold transition duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={handleBackToLogin}
                  className="text-[#9D282C] font-semibold hover:underline text-sm"
                >
                  ← Back to Login
                </button>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <svg
                  className="w-12 h-12 text-green-500 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  Email Sent!
                </h3>
                <p className="text-green-700 text-sm">
                  Password reset link has been sent to:
                </p>
                <p className="text-green-800 font-semibold mt-1">{maskedEmail}</p>
              </div>

              <p className="text-gray-600 text-sm mb-4">
                Please check your email and click the link to reset your password.
                The link will expire in 30 minutes.
              </p>

              <button
                onClick={handleBackToLogin}
                className="bg-[#9D282C] text-white w-full py-3 px-4 rounded-lg font-semibold transition duration-200 hover:shadow-lg"
              >
                Back to Login
              </button>
            </div>
          )}
        </div>

        {/* Powered By Footer */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-3 whitespace-nowrap">
            <span className="text-xs text-gray-500">Powered by</span>
            <img
              src={IzyaneLogo}
              alt="Izyane"
              className="h-3 object-contain inline-block"
            />
            <span className="text-xs text-gray-500">&</span>
            <img
              src={rolafaceLogo}
              alt="Rolaface"
              className="h-1.5 object-contain inline-block"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;